/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useMemo, type CSSProperties, type ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { migrateDoc } from '@/features/studio/model/doc';
import { compileTheme, type CompiledTheme } from '@/features/studio/model/compile';

/**
 * The Identity Layer: compiles a community's published theme snapshot
 * (`communities.theme_doc`) into scoped CSS variables + a context every
 * community-view component can consume. The personal skin stays the global
 * `:root` base layer underneath; this scope overrides *inside* the community
 * only. Same compiler as the studio canvas — the preview IS production.
 */
const CommunityThemeContext = createContext<CompiledTheme | null>(null);

/** Compiled theme for the surrounding community scope (null = unthemed default). */
export function useCommunityTheme(): CompiledTheme | null {
  return useContext(CommunityThemeContext);
}

export function CommunityThemeScope({
  doc,
  className,
  children,
}: {
  /** Raw `communities.theme_doc` JSON — validated/upgraded here, never trusted. */
  doc: unknown;
  className?: string;
  children: ReactNode;
}) {
  const compiled = useMemo(() => (doc ? compileTheme(migrateDoc(doc)) : null), [doc]);

  if (!compiled) return <>{children}</>;

  return (
    <div className={cn('contents', compiled.fontClass, className)} style={compiled.vars as CSSProperties}>
      <CommunityThemeContext.Provider value={compiled}>{children}</CommunityThemeContext.Provider>
    </div>
  );
}

/** Inline CSS clipping an accent→white gradient into text (the "gradient" community title). */
export function gradientTextStyle(accent: string): CSSProperties {
  return {
    backgroundImage: `linear-gradient(90deg, ${accent}, #ffffff)`,
    WebkitBackgroundClip: 'text',
    backgroundClip: 'text',
    color: 'transparent',
  };
}

/**
 * The theme's background photo layer — rendered inside the community shell so
 * it shows crisp (above the frosted backdrop, below all content), matching the
 * studio canvas exactly: dim scrim, accent tint wash, optional vignette.
 */
export function CommunityThemeBackdrop() {
  const t = useCommunityTheme();
  if (!t?.backgroundImage) return null;
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0 z-0">
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `${t.dimOverlay}, url(${t.backgroundImage})` }}
      />
      {t.tintOpacity > 0 && (
        <div
          className="absolute inset-0"
          style={{ background: t.accent, opacity: t.tintOpacity, mixBlendMode: 'soft-light' }}
        />
      )}
      {t.vignette && (
        <div className="absolute inset-0" style={{ boxShadow: 'inset 0 0 140px 50px rgba(0,0,0,0.55)' }} />
      )}
    </div>
  );
}
